/**
 * API Error Class
 * Rule: ERROR PROPAGATION - Transform and re-throw, never swallow
 */
export class ApiError extends Error {
    constructor(message, statusCode = 500, code = 'UNKNOWN_ERROR', original = null) {
        super(message);
        this.name = 'ApiError';
        this.statusCode = statusCode;
        this.code = code;
        this.original = original;
        this.details = null;
        this.timestamp = new Date().toISOString();
    }

    /**
     * Create ApiError from axios/fetch response
     */
    static fromResponse(error) {
        const axiosError = error?.originalError ?? error?.original ?? error;
        const response = axiosError?.response ?? error?.response;
        const data = response?.data;
        const statusCode = response?.status ?? error?.status ?? error?.statusCode ?? 500;
        const message =
            (typeof data?.error === 'string' && data.error) ||
            data?.message ||
            error?.message ||
            'An error occurred';
        const code = data?.code || 'API_ERROR';
        const details = data?.details ?? data ?? null;

        const apiError = new ApiError(message, statusCode, code, error);
        apiError.details = details;
        return apiError;
    }

    /**
     * Create validation error
     */
    static validation(message, errors = {}) {
        const error = new ApiError(message, 400, 'VALIDATION_ERROR');
        error.validationErrors = errors;
        return error;
    }

    /**
     * Create not found error
     */
    static notFound(resource = 'Resource') {
        return new ApiError(`${resource} not found`, 404, 'NOT_FOUND');
    }

    /**
     * Create unauthorized error
     */
    static unauthorized(message = 'Unauthorized') {
        return new ApiError(message, 401, 'UNAUTHORIZED');
    }

    /**
     * Create forbidden error
     */
    static forbidden(message = 'Forbidden') {
        return new ApiError(message, 403, 'FORBIDDEN');
    }

    // Helper methods
    isAuthError() {
        return this.statusCode === 401 || this.statusCode === 403;
    }

    isNotFound() {
        return this.statusCode === 404;
    }

    isValidationError() {
        // Only client/SDK Zod failures — API 400 business errors (duplicate key, etc.)
        // must keep their message for toast/UI.
        return this.code === 'VALIDATION_ERROR';
    }

    isServerError() {
        return this.statusCode >= 500;
    }

    toJSON() {
        return {
            name: this.name,
            message: this.message,
            statusCode: this.statusCode,
            code: this.code,
            timestamp: this.timestamp,
            details: this.details,
            validationErrors: this.validationErrors
        };
    }
}
